import { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Camera, Save, UserCircle } from "lucide-react";
import LiftMaxesEditor from "@/components/LiftMaxesEditor";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function StudentProfile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pictureData, setPictureData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const fileRef = useRef(null);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file || file.size > 5 * 1024 * 1024) {
      toast.error("Image max 5 Mo");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      setPictureData(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: name.trim() };
      if (pictureData) payload.picture_data = pictureData;
      await api.put("/auth/update-profile", payload);
      await refreshUser();
      toast.success("Profil mis à jour");
    } catch {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Mot de passe modifié");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setChangingPw(false);
    }
  };

  const avatarSrc = preview || (user?.picture ? `${API_BASE}${user.picture}` : null);

  return (
    <div>
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
          <UserCircle className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mon Compte</h1>
          <p className="text-slate-400 text-sm mt-1">{user?.email}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-slate-100">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Profil</h2>
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={avatarSrc} />
                  <AvatarFallback className="bg-purple-100 text-purple-600 text-xl">{name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                  <Camera className="w-4 h-4 mr-2" /> Photo
                </Button>
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" />
              </div>
              <Button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white rounded-full">
                <Save className="w-4 h-4 mr-2" /> {saving ? "..." : "Enregistrer"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Mot de passe</h2>
            <form onSubmit={changePassword} className="space-y-4">
              <div>
                <Label>Mot de passe actuel</Label>
                <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>Nouveau mot de passe</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} minLength={8} className="mt-1.5 h-11" />
              </div>
              <Button type="submit" disabled={changingPw} variant="outline" className="rounded-full">
                {changingPw ? "..." : "Changer le mot de passe"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-100 md:col-span-2">
          <CardContent className="p-6">
            <LiftMaxesEditor />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}